import { useUiText } from "../../locale/UiLocale";
import { type ReactNode, useId } from "react";
import { Icon } from "../../icons";
import { FieldContext } from "./FieldContext";

export { useFieldContext } from "./FieldContext";

export interface FieldProps {
  label: ReactNode;
  /** Optional explicit id for the control; generated otherwise. */
  id?: string;
  help?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  /** Extra content aligned to the right of the label (e.g. a link). */
  labelAside?: ReactNode;
  /** Keep the label for assistive tech only (the context already titles the control). */
  hideLabel?: boolean;
  children: ReactNode;
  className?: string;
}

/** Label + control + help text + inline error, wired with ids and aria attributes. */
export function Field({ label, id, help, error, optional, labelAside, hideLabel, children, className }: FieldProps): React.JSX.Element {
  const t = useUiText();
  const generated = useId();
  const controlId = id ?? `f${generated.replace(/:/g, "")}`;
  const helpId = help ? `${controlId}-help` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ id: controlId, describedBy, invalid: Boolean(error) }}>
      <div className={`nb-field${className ? ` ${className}` : ""}`}>
        <div className={hideLabel ? "nb-sr" : "nb-label-row"}>
          <label className="nb-label" htmlFor={controlId} id={`${controlId}-label`}>
            {label}
            {optional ? <span className="nb-label-opt"> {t("(optional)")}</span> : null}
          </label>
          {labelAside}
        </div>
        {children}
        <FieldMessage error={error} errorId={errorId} help={help} helpId={helpId} />
      </div>
    </FieldContext.Provider>
  );
}

interface FieldMessageProps {
  error?: ReactNode;
  errorId?: string;
  help?: ReactNode;
  helpId?: string;
}

/** The error replaces the help text while there is one. */
function FieldMessage({ error, errorId, help, helpId }: FieldMessageProps): React.JSX.Element | null {
  if (error) {
    return (
      <p className="nb-error-text" id={errorId} role="alert">
        <Icon name="alertCircle" />
        <span>{error}</span>
      </p>
    );
  }
  if (!help) return null;
  return (
    <p className="nb-help" id={helpId}>
      {help}
    </p>
  );
}
